/**
 * Turn UX-A A.2 — Modal de rattachement contact/chantier pour un devis dicté.
 *
 * Modes exclusifs :
 * - Tab "Contact existant" : recherche → sélection contact → sélection chantier existant
 * - Tab "Nouveau contact" : formulaire minimal (cascade quick contact + chantier)
 *
 * Submit → POST /api/quotes/from-dictation → redirection vers /quotes/{id}
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, UserPlus, Check, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function AttachQuoteModal({ open, onOpenChange, lines, rawNotes, transcription }) {
  const nav = useNavigate();
  const [tab, setTab] = useState("existing");
  const [submitting, setSubmitting] = useState(false);

  // Tab "Existant"
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState(null);

  // Tab "Nouveau"
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [chantierTitle, setChantierTitle] = useState("");
  const [chantierAddress, setChantierAddress] = useState("");

  // Reset état au open/close
  useEffect(() => {
    if (!open) {
      setTab("existing");
      setSearchQ("");
      setSearchResults([]);
      setSelectedContact(null);
      setProjects([]);
      setSelectedProject(null);
      setChantiers([]);
      setSelectedChantier(null);
      setFirstName("");
      setLastName("");
      setPhone("");
      setEmail("");
      setChantierTitle("");
      setChantierAddress("");
    }
  }, [open]);

  // Recherche contacts (debounce 300ms)
  useEffect(() => {
    if (tab !== "existing" || !searchQ.trim() || searchQ.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/contacts", { params: { search: searchQ.trim(), limit: 10 } });
        setSearchResults(data.items || []);
      } catch (e) {
        toast.error("Recherche échouée", { description: formatApiError(e) });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, tab]);

  // Load projects quand contact sélectionné
  useEffect(() => {
    if (!selectedContact) {
      setProjects([]);
      setSelectedProject(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/contacts/${selectedContact.id}`);
        setProjects((data.projects || []).filter((p) => !p.closed));
      } catch (e) {
        toast.error("Chargement projets échoué", { description: formatApiError(e) });
      }
    })();
  }, [selectedContact]);

  // Load chantiers quand projet sélectionné
  useEffect(() => {
    if (!selectedProject) {
      setChantiers([]);
      setSelectedChantier(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/chantiers", { params: { lead_id: selectedProject.id } });
        setChantiers(data || []);
      } catch (e) {
        toast.error("Chargement chantiers échoué", { description: formatApiError(e) });
      }
    })();
  }, [selectedProject]);

  const canSubmitExisting = selectedContact && selectedChantier;
  const canSubmitQuick =
    firstName.trim().length > 0 && lastName.trim().length > 0 && chantierTitle.trim().length > 0;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = {
        lines: lines || [],
        raw_notes: rawNotes || "",
        transcription: transcription || null,
      };
      if (tab === "existing") {
        body.contact_id = selectedContact.id;
        body.chantier_id = selectedChantier.id;
      } else {
        body.quick_contact = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
        };
        body.quick_chantier = {
          title: chantierTitle.trim(),
          address: chantierAddress.trim() || null,
        };
      }
      const { data } = await api.post("/quotes/from-dictation", body);
      toast.success("Devis créé", {
        description: "N'oubliez pas d'ajouter les prix unitaires manquants.",
      });
      onOpenChange(false);
      nav(data.redirect_url);
    } catch (e) {
      toast.error("Création échouée", { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-slate-700 bg-slate-900 text-slate-100"
        data-testid="attach-quote-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Créer le devis</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Rattachez ce brouillon dicté à un contact + chantier existant, ou créez-en de
            nouveaux à la volée.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger
              value="existing"
              className="data-[state=active]:bg-[#C4553B] data-[state=active]:text-white"
              data-testid="attach-tab-existing"
            >
              <Search className="mr-1.5 h-4 w-4" />
              Contact existant
            </TabsTrigger>
            <TabsTrigger
              value="quick"
              className="data-[state=active]:bg-[#C4553B] data-[state=active]:text-white"
              data-testid="attach-tab-quick"
            >
              <UserPlus className="mr-1.5 h-4 w-4" />
              Nouveau contact
            </TabsTrigger>
          </TabsList>

          {/* Tab existant */}
          <TabsContent value="existing" className="mt-4 space-y-4">
            <div>
              <Label className="text-xs text-slate-400">Rechercher un contact</Label>
              <Input
                value={searchQ}
                onChange={(e) => {
                  setSearchQ(e.target.value);
                  setSelectedContact(null);
                }}
                placeholder="Nom, email, téléphone…"
                className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                data-testid="attach-search-input"
                autoComplete="off"
              />
              {searching && (
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" /> Recherche…
                </p>
              )}
            </div>

            {searchResults.length > 0 && !selectedContact && (
              <div className="max-h-40 overflow-y-auto rounded border border-slate-700 bg-slate-800/40">
                {searchResults.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedContact(c)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-700/60"
                    data-testid={`attach-result-${c.id}`}
                  >
                    <span>
                      <span className="font-medium text-slate-100">
                        {c.first_name} {c.last_name}
                      </span>
                      {c.email && <span className="ml-2 text-xs text-slate-400">{c.email}</span>}
                    </span>
                    <span className="text-xs text-slate-500">{c.projects_count} projet(s)</span>
                  </button>
                ))}
              </div>
            )}

            {selectedContact && (
              <div className="rounded border border-[#C4553B]/40 bg-[#C4553B]/10 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-100">
                    <Check className="mr-1 inline h-3.5 w-3.5 text-[#C4553B]" />
                    {selectedContact.first_name} {selectedContact.last_name}
                  </span>
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="text-xs text-slate-400 underline hover:text-slate-200"
                    data-testid="attach-change-contact"
                  >
                    Changer
                  </button>
                </div>
              </div>
            )}

            {selectedContact && projects.length === 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-400">
                <AlertCircle className="h-3.5 w-3.5" />
                Aucun projet ouvert pour ce contact. Utilisez l'onglet "Nouveau contact" ou
                créez d'abord un projet depuis la fiche contact.
              </p>
            )}

            {selectedContact && projects.length > 0 && (
              <div>
                <Label className="text-xs text-slate-400">Projet</Label>
                <div className="mt-1 max-h-32 space-y-1 overflow-y-auto">
                  {projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedProject(p)}
                      className={`w-full rounded border px-3 py-1.5 text-left text-sm transition-colors ${
                        selectedProject?.id === p.id
                          ? "border-[#C4553B] bg-[#C4553B]/10 text-slate-100"
                          : "border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-700/40"
                      }`}
                      data-testid={`attach-project-${p.id}`}
                    >
                      {p.title || p.project_description?.slice(0, 60) || "Projet sans titre"}
                      <span className="ml-2 text-xs text-slate-500">· {p.stage}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedProject && chantiers.length > 0 && (
              <div>
                <Label className="text-xs text-slate-400">Chantier</Label>
                <div className="mt-1 max-h-32 space-y-1 overflow-y-auto">
                  {chantiers.map((ch) => (
                    <button
                      key={ch.id}
                      onClick={() => setSelectedChantier(ch)}
                      className={`w-full rounded border px-3 py-1.5 text-left text-sm transition-colors ${
                        selectedChantier?.id === ch.id
                          ? "border-[#C4553B] bg-[#C4553B]/10 text-slate-100"
                          : "border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-700/40"
                      }`}
                      data-testid={`attach-chantier-${ch.id}`}
                    >
                      {ch.name}
                      {ch.reference_code && (
                        <span className="ml-2 text-xs text-slate-500">· {ch.reference_code}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedProject && chantiers.length === 0 && (
              <p className="flex items-center gap-1 text-xs text-amber-400">
                <AlertCircle className="h-3.5 w-3.5" />
                Aucun chantier sur ce projet. Créez-en un depuis la fiche projet.
              </p>
            )}
          </TabsContent>

          {/* Tab nouveau */}
          <TabsContent value="quick" className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Prénom *</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                  data-testid="attach-quick-firstname"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Nom *</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                  data-testid="attach-quick-lastname"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Téléphone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                  data-testid="attach-quick-phone"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                  data-testid="attach-quick-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Titre du chantier *</Label>
              <Input
                value={chantierTitle}
                onChange={(e) => setChantierTitle(e.target.value)}
                placeholder="Ex : Toiture rue de la Loi"
                className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                data-testid="attach-quick-chantier-title"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Adresse du chantier</Label>
              <Input
                value={chantierAddress}
                onChange={(e) => setChantierAddress(e.target.value)}
                placeholder="Adresse complète (facultatif)"
                className="mt-1 border-slate-600 bg-slate-800/60 text-slate-100"
                data-testid="attach-quick-chantier-address"
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-300 hover:bg-slate-800 hover:text-slate-100"
            data-testid="attach-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            disabled={
              submitting || (tab === "existing" ? !canSubmitExisting : !canSubmitQuick)
            }
            onClick={handleSubmit}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C] disabled:opacity-50"
            data-testid="attach-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Création…
              </>
            ) : (
              "Créer le devis"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
